import type {
  Feature,
  FeatureCollection,
  Point,
  Polygon,
  MultiPolygon,
} from 'geojson';

const ISAD_QUERY_URL =
  'https://geoportal.npu.cz/arcgis/rest/services/Tematicke/CP_UAN/MapServer/0/query';
const POLYGON_BATCH_SIZE = 200;
const OVERVIEW_PAGE_SIZE = 2000;
const REQUEST_CONCURRENCY = 4;

export const ISAD_POLYGON_ZOOM = 10;

export interface IsadBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export type IsadFeature = Feature<
  Polygon | MultiPolygon,
  {
    ID_polygonu: number;
    ID_SAS?: number;
    Nazev?: string;
    Kategorie?: string;
  }
>;

export type IsadOverview = FeatureCollection<Point>;

interface QueryResponse {
  objectIds?: number[] | null;
  features?: IsadFeature[];
  exceededTransferLimit?: boolean;
  error?: { message?: string };
}

async function queryIsad(
  params: Record<string, string>,
  signal: AbortSignal
): Promise<QueryResponse> {
  const response = await fetch(
    `${ISAD_QUERY_URL}?${new URLSearchParams(params)}`,
    { signal }
  );

  if (!response.ok) {
    throw new Error(`ISAD request failed (${response.status}).`);
  }

  const payload: QueryResponse = await response.json();

  if (!payload || payload.error) {
    throw new Error('ISAD service returned an error.');
  }

  return payload;
}

async function getIsadIds(signal: AbortSignal, bounds?: IsadBounds) {
  const params: Record<string, string> = {
    f: 'json',
    where: '1=1',
    returnIdsOnly: 'true',
  };

  if (bounds) {
    params.geometry = [
      bounds.west,
      bounds.south,
      bounds.east,
      bounds.north,
    ].join(',');
    params.geometryType = 'esriGeometryEnvelope';
    params.spatialRel = 'esriSpatialRelEnvelopeIntersects';
    params.inSR = '4326';
  }

  const payload = await queryIsad(params, signal);

  // ArcGIS returns null, rather than an empty array, outside its coverage.
  if (payload.objectIds === null) {
    return [];
  }

  if (
    !Array.isArray(payload.objectIds) ||
    !payload.objectIds.every(Number.isSafeInteger)
  ) {
    throw new Error('ISAD service returned invalid polygon IDs.');
  }

  return [...new Set(payload.objectIds)].sort((a, b) => a - b);
}

function readFeatures(payload: QueryResponse): IsadFeature[] {
  if (
    !Array.isArray(payload.features) ||
    payload.features.some(
      (feature) =>
        feature?.type !== 'Feature' ||
        !Number.isSafeInteger(feature.properties?.ID_polygonu) ||
        !['Polygon', 'MultiPolygon'].includes(feature.geometry?.type) ||
        !Array.isArray(feature.geometry.coordinates)
    )
  ) {
    throw new Error('ISAD service returned invalid polygons.');
  }

  return payload.features;
}

async function loadBatches(
  ids: number[],
  batchSize: number,
  load: (batch: number[], offset: number) => Promise<IsadFeature[]>,
  signal: AbortSignal
) {
  const features: IsadFeature[] = [];
  let nextOffset = 0;

  const worker = async () => {
    while (nextOffset < ids.length) {
      if (signal.aborted) {
        throw new DOMException('ISAD request aborted.', 'AbortError');
      }

      const offset = nextOffset;
      nextOffset += batchSize;
      features.push(
        ...(await load(ids.slice(offset, offset + batchSize), offset))
      );
    }
  };

  await Promise.all(Array.from({ length: REQUEST_CONCURRENCY }, worker));

  const byId = new Map(
    features.map((feature) => [feature.properties.ID_polygonu, feature])
  );

  // Never replace a complete viewport with a truncated or inconsistent response.
  if (byId.size !== ids.length || ids.some((id) => !byId.has(id))) {
    throw new Error('ISAD service returned an incomplete polygon set.');
  }

  return ids.map((id) => byId.get(id)!);
}

export async function fetchIsadPolygons(
  bounds: IsadBounds,
  signal: AbortSignal,
  cachedFeatures: IsadFeature[] = []
): Promise<IsadFeature[]> {
  const ids = await getIsadIds(signal, bounds);
  const cachedById = new Map(
    cachedFeatures.map((feature) => [feature.properties.ID_polygonu, feature])
  );
  const missingIds = ids.filter((id) => !cachedById.has(id));
  const features = await loadBatches(
    missingIds,
    POLYGON_BATCH_SIZE,
    async (batch) => {
      const payload = await queryIsad(
        {
          f: 'geojson',
          objectIds: batch.join(','),
          outSR: '4326',
          outFields: 'ID_polygonu,ID_SAS,Nazev,Kategorie',
          returnGeometry: 'true',
          returnM: 'false',
          returnZ: 'false',
        },
        signal
      );

      if (payload.exceededTransferLimit) {
        throw new Error('ISAD polygon batch exceeded the service limit.');
      }

      return readFeatures(payload);
    },
    signal
  );

  features.forEach((feature) =>
    cachedById.set(feature.properties.ID_polygonu, feature)
  );

  return ids.map((id) => cachedById.get(id)!);
}

export async function fetchIsadOverview(
  signal: AbortSignal
): Promise<IsadOverview> {
  const ids = await getIsadIds(signal);
  const features = await loadBatches(
    ids,
    OVERVIEW_PAGE_SIZE,
    async (_batch, offset) => {
      const payload = await queryIsad(
        {
          f: 'geojson',
          where: '1=1',
          outSR: '4326',
          outFields: 'ID_polygonu',
          returnGeometry: 'true',
          returnM: 'false',
          returnZ: 'false',
          maxAllowableOffset: '0.01',
          geometryPrecision: '5',
          orderByFields: 'ID_polygonu',
          resultOffset: String(offset),
          resultRecordCount: String(OVERVIEW_PAGE_SIZE),
        },
        signal
      );

      return readFeatures(payload);
    },
    signal
  );

  return {
    type: 'FeatureCollection',
    features: features.map((feature) => {
      const polygons =
        feature.geometry.type === 'Polygon'
          ? [feature.geometry.coordinates]
          : feature.geometry.coordinates;
      let west = Infinity;
      let south = Infinity;
      let east = -Infinity;
      let north = -Infinity;

      for (const polygon of polygons) {
        for (const ring of polygon) {
          for (const [longitude, latitude] of ring) {
            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
              throw new Error('ISAD service returned invalid coordinates.');
            }

            west = Math.min(west, longitude);
            south = Math.min(south, latitude);
            east = Math.max(east, longitude);
            north = Math.max(north, latitude);
          }
        }
      }

      if (!Number.isFinite(west)) {
        throw new Error('ISAD service returned an empty polygon.');
      }

      return {
        type: 'Feature',
        id: feature.properties.ID_polygonu,
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [(west + east) / 2, (south + north) / 2],
        },
      };
    }),
  };
}

export function containsIsadBounds(outer: IsadBounds, inner: IsadBounds) {
  return (
    outer.west <= inner.west &&
    outer.south <= inner.south &&
    outer.east >= inner.east &&
    outer.north >= inner.north
  );
}
